<template>
  <div v-if="visible" class="first-run-sync-modal-overlay">
    <div class="first-run-sync-modal">
      <!-- Header -->
      <div class="modal-header">
        <h1 class="app-title">SentryKit</h1>
        <span class="user-email">{{ userEmail }}</span>
      </div>

      <!-- Syncing State -->
      <div v-if="!syncComplete" class="modal-body syncing">
        <h2 class="modal-title">Setting up your workspace....</h2>
        <p class="modal-description">
          Syncing your Amazon catalog and setting up real-time Buy Box monitoring. Takes 2 - 5 minutes for most accounts.
        </p>

        <ul class="sync-steps">
          <li v-for="(step, index) in derivedSteps" :key="step.id" class="sync-step">
            <i v-if="step.completed" class="check circle icon green"></i>
            <i v-else-if="step.inProgress" class="circle notch loading icon"></i>
            <i v-else class="circle outline icon grey"></i>
            <span>{{ step.completed ? step.completedLabel : step.label }}</span>
          </li>
        </ul>

        <div class="progress-section">
          <span class="progress-text">Item {{ completedStepsCount }}/{{ derivedSteps.length }} complete</span>
          <div class="progress-bar-container">
            <div class="progress-bar-fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
        </div>

        <button class="go-to-dashboard-btn" @click="goToDashboard">
          Go to Dashboard
          <i class="arrow right icon"></i>
        </button>
      </div>

      <!-- Ready State -->
      <div v-else class="modal-body ready">
        <div class="sync-complete-banner">
          <i class="check circle icon"></i>
          <span>Sync complete</span>
        </div>
        <p class="ready-message">All 90-day Buy Box monitoring and historic data loaded!</p>

        <ul class="sync-steps">
          <li v-for="step in derivedSteps" :key="step.id" class="sync-step completed">
            <i class="check circle icon green"></i>
            <span>{{ step.completedLabel }}</span>
          </li>
        </ul>

        <button class="go-to-dashboard-btn ready-btn" @click="dismiss">
          <i class="check circle icon"></i>
          Your dashboard is ready - Go to Dashboard
        </button>
      </div>

      <p class="footer-text">All data secured.</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FirstRunSyncModal',
  props: {
    userEmail: {
      type: String,
      default: ''
    },
    onboardingStatus: {
      type: Object,
      default: function () {
        return {
          sellerConnected: false,
          productsImported: false,
          buyboxFetched: false,
          ordersFetched: false,
          completed: false
        }
      }
    },
    onDismiss: {
      type: Function,
      default: () => {}
    }
  },
  data: function () {
    return {
      visible: true,
      syncComplete: false,
      syncSteps: [
        { id: 'account', label: 'Amazon account connected', completedLabel: 'Amazon account connected' },
        { id: 'products', label: 'Products imported', completedLabel: 'Products imported' },
        { id: 'buybox', label: 'Reading current Buy Box status', completedLabel: 'Buy Box status read' },
        { id: 'sales', label: 'Fetching 30-day sales history', completedLabel: '90-day sales history fetched' },
      ]
    }
  },
  computed: {
    derivedSteps: function () {
      var s = this.onboardingStatus || {}
      return this.syncSteps.map(function (step) {
        var completed = false
        var inProgress = false
        if (step.id === 'account') {
          completed = !!s.sellerConnected
          inProgress = !completed
        } else if (step.id === 'products') {
          completed = !!s.productsImported
          inProgress = !!s.sellerConnected && !completed
        } else if (step.id === 'buybox') {
          completed = !!s.buyboxFetched
          inProgress = !!s.productsImported && !completed
        } else if (step.id === 'sales') {
          completed = !!s.ordersFetched
          inProgress = !!s.buyboxFetched && !completed
        } else if (step.id === 'intelligence') {
          completed = !!s.completed
          inProgress = !!s.ordersFetched && !completed
        }
        return { ...step, completed: completed, inProgress: inProgress }
      })
    },
    completedStepsCount: function () {
      return this.derivedSteps.filter(function (s) { return s.completed }).length
    },
    progressPercent: function () {
      return this.derivedSteps.length ? Math.round((this.completedStepsCount / this.derivedSteps.length) * 100) : 0
    }
  },
  watch: {
    'onboardingStatus.completed': function (val) {
      if (val) this.syncComplete = true
    }
  },
  created: function () {
    if (this.onboardingStatus && this.onboardingStatus.completed) {
      this.syncComplete = true
    }
  },
  methods: {
    goToDashboard: function () {
      // User can explore while sync continues - just dismiss the modal
      this.dismiss()
    },
    dismiss: function () {
      this.visible = false
      this.onDismiss()
    }
  }
}
</script>

<style scoped>
.first-run-sync-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, #1e3a5f 0%, #0b1929 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.first-run-sync-modal {
  background: #1a2d47;
  border-radius: 12px;
  max-width: 480px;
  width: 90%;
  padding: 32px;
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.4);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.app-title {
  font-size: 24px;
  font-weight: 700;
  color: #f97316;
  margin: 0;
}

.user-email {
  font-size: 12px;
  color: #94a3b8;
}

.modal-body {
  color: #e2e8f0;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #f8fafc;
}

.modal-description {
  font-size: 14px;
  color: #94a3b8;
  margin: 0 0 24px 0;
  line-height: 1.5;
}

.sync-steps {
  list-style: none;
  padding: 0;
  margin: 0 0 24px 0;
}

.sync-step {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  font-size: 14px;
  font-weight: 500;
}

.sync-step i.green {
  color: #22c55e;
  flex-shrink: 0;
}

.sync-step i.grey {
  color: #64748b;
  flex-shrink: 0;
}

.sync-step i.loading {
  color: #f97316;
  flex-shrink: 0;
}

.progress-section {
  margin-bottom: 24px;
}

.progress-text {
  display: block;
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 8px;
}

.progress-bar-container {
  height: 8px;
  background: #334155;
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #f97316 0%, #fb923c 100%);
  border-radius: 4px;
  transition: width 0.4s ease;
}

.go-to-dashboard-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 24px;
  background: #f97316;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.go-to-dashboard-btn:hover {
  background: #ea580c;
}

.go-to-dashboard-btn.ready-btn {
  background: #22c55e;
  margin-top: 8px;
}

.go-to-dashboard-btn.ready-btn:hover {
  background: #16a34a;
}

.sync-complete-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(34, 197, 94, 0.2);
  border: 1px solid #22c55e;
  border-radius: 8px;
  margin-bottom: 16px;
  color: #22c55e;
  font-weight: 600;
  font-size: 14px;
}

.ready-message {
  font-size: 14px;
  color: #94a3b8;
  margin: 0 0 24px 0;
}

.footer-text {
  margin-top: 24px;
  font-size: 11px;
  color: #64748b;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// FirstRunSyncModal.vue?45ca08e9